'use client';

import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { Region } from '@/components/region';
import { Link } from '@/shared-ui/atoms';
import { MenuItem } from '@/shared-ui/molecules/menu-item';
import {
    contentSlotRegionHasComponents,
    type ContentSlotPageWithComponentData,
} from '@/extensions/snk/lib/content-slot-page';
import { BurgerMenuSubmenuHeader } from '../submenu-header';
import { BurgerMenuSubmenuPdContent } from '../submenu-pd-content';
import { BurgerMenuSubmenuContentSkeleton } from '../skeleton';
/** @sfdc-extension-line SNK */
import { getBrandsSubmenuRegionId } from '@/extensions/snk/lib/brands-submenu-regions';
/** @sfdc-extension-line SNK */
import { getCategoryHref } from '@/extensions/snk/lib/utils';
/** @sfdc-extension-line SNK */
import { getCuratedBrandCategories, type BrandsCuratedVariant } from '@/extensions/snk/lib/brands-utils';

type BurgerMenuBrandsCuratedProps = {
    brandsCategory: ShopperProducts.schemas['Category'];
    variant: BrandsCuratedVariant;
    contentSlots?: ContentSlotPageWithComponentData;
    onBack: () => void;
    onCloseMenu: () => void;
};

const CURATED_TITLE_KEY: Record<BrandsCuratedVariant, string> = {
    affordable: 'burgerMenu.affordableBrands',
    bestselling: 'burgerMenu.bestsellingBrands',
    new: 'burgerMenu.newToSpaceNk',
};

const CURATED_DEFAULT_TITLE: Record<BrandsCuratedVariant, string> = {
    affordable: 'Affordable Brands',
    bestselling: 'Bestselling Brands',
    new: 'New to Space NK',
};

export const BurgerMenuBrandsCurated = ({
    brandsCategory,
    variant,
    contentSlots,
    onBack,
    onCloseMenu,
}: BurgerMenuBrandsCuratedProps): ReactElement => {
    const { t } = useTranslation('header');

    const brands = useMemo(() => getCuratedBrandCategories(brandsCategory, variant), [brandsCategory, variant]);

    const brandsSubmenuRegionId = getBrandsSubmenuRegionId(variant);
    const hasBrandsSubmenuContent = contentSlotRegionHasComponents(contentSlots, brandsSubmenuRegionId);

    return (
        <div className="flex flex-col">
            <BurgerMenuSubmenuHeader
                title={t(CURATED_TITLE_KEY[variant], CURATED_DEFAULT_TITLE[variant])}
                onBack={onBack}
            />
            <ul className="list-none">
                {brands.map((brand) => (
                    <li key={brand.id}>
                        <MenuItem
                            textAlign="left"
                            textVariant="h4"
                            showBottomBorder
                            className="rounded-none uppercase"
                            asChild>
                            <Link to={getCategoryHref(brand)} onClick={onCloseMenu}>
                                {brand.name}
                            </Link>
                        </MenuItem>
                    </li>
                ))}
            </ul>

            {hasBrandsSubmenuContent && contentSlots ? (
                <BurgerMenuSubmenuPdContent testId="burger-menu-brands-submenu-content">
                    <Region
                        page={contentSlots}
                        regionId={brandsSubmenuRegionId}
                        fallbackElement={<BurgerMenuSubmenuContentSkeleton />}
                        errorElement={null}
                    />
                </BurgerMenuSubmenuPdContent>
            ) : null}
        </div>
    );
};
